import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Select, InputNumber, Row, Col, Divider, Typography, Tag, Button, message, Table } from 'antd';
import { ShareAltOutlined, CopyOutlined, LinkOutlined, EyeOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

// 等级：fee=升级费用；rate=推荐费费率；downRate=下级TS总额分成（仅钻石起）
const TIERS = [
  { key: '普通代理', label: '普通', fee: 298, rate: 0.02, downRate: 0, color: '#8c8c8c', grad: 'linear-gradient(135deg,#c9c9c9,#8c8c8c)' },
  { key: '银牌代理', label: '银牌', fee: 598, rate: 0.04, downRate: 0, color: '#4096ff', grad: 'linear-gradient(135deg,#91caff,#4096ff)' },
  { key: '金牌代理', label: '金牌', fee: 998, rate: 0.06, downRate: 0, color: '#faad14', grad: 'linear-gradient(135deg,#ffd666,#faad14)' },
  { key: '钻石代理', label: '钻石', fee: 1998, rate: 0.08, downRate: 0.01, color: '#722ed1', grad: 'linear-gradient(135deg,#b37feb,#722ed1)' },
  { key: '铂金代理', label: '铂金', fee: 3998, rate: 0.10, downRate: 0.02, color: '#08979c', grad: 'linear-gradient(135deg,#5cdbd3,#08979c)' },
];
const TIER_MAP = Object.fromEntries(TIERS.map((t) => [t.key, t]));

// 数字滚动动效
function useCountUp(value, duration = 600) {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const startRef = useRef(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const from = fromRef.current;
    const to = Number(value) || 0;
    startRef.current = 0;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min(1, (ts - startRef.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = from + (to - from) * eased;
      setDisplay(cur);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);
  return display;
}

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('zh-CN', { maximumFractionDigits: 0 });

// 复制到剪贴板（带降级方案）
async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* 降级 */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}

// 分享面板：生成一个随机访问密码，访客需在 /ts-demo 输入密码才能查看
function DemoSharePanel() {
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(null); // { code, url }

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post('/ts/demo-share', {});
      if (res.data && res.data.success) {
        const origin = window.location.origin;
        const url = `${origin}${res.data.path || '/ts-demo'}`;
        setShare({ code: res.data.code, url });
      } else {
        message.error(res.data?.message || '生成失败');
      }
    } catch (e) {
      message.error(e.response?.data?.message || '生成失败');
    } finally {
      setLoading(false);
    }
  };

  const doCopy = async (text, label) => {
    const ok = await copyText(text);
    ok ? message.success(`${label}已复制`) : message.error('复制失败，请手动选择');
  };

  return (
    <div style={{ marginTop: 18, background: '#f6ffed', border: '1px solid #d9f7be', borderRadius: 14, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Text strong style={{ fontSize: 14 }}><ShareAltOutlined /> 加密分享给客户 / 意向代理</Text>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>生成一个随机访问密码，对方需输入密码才能打开演示页</div>
        </div>
        <Button type="primary" icon={<ShareAltOutlined />} loading={loading} onClick={generate}>
          {share ? '重新生成密码' : '生成加密分享链接'}
        </Button>
      </div>

      {share && (
        <div style={{ marginTop: 14, background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #eaeaea' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <LinkOutlined style={{ color: '#52c41a' }} />
            <Text style={{ wordBreak: 'break-all', flex: 1, minWidth: 180 }}>{share.url}</Text>
            <Button size="small" icon={<CopyOutlined />} onClick={() => doCopy(share.url, '链接')}>复制链接</Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#8c8c8c' }}>访问密码</span>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: 4, color: '#08979c', fontFamily: 'monospace' }}>{share.code}</span>
            <Button size="small" icon={<CopyOutlined />} onClick={() => doCopy(share.code, '密码')}>复制密码</Button>
            <Button size="small" type="link" onClick={() => doCopy(`二次TS·收益演示\n链接：${share.url}\n访问密码：${share.code}`, '链接和密码')}>复制「链接+密码」</Button>
          </div>
          <div style={{ fontSize: 12, color: '#bbb', marginTop: 8 }}>提示：每次生成都是新密码，旧密码仍然有效；如需作废可在后端停用。</div>
        </div>
      )}
    </div>
  );
}

// 浏览统计（仅管理员可见）：浏览量 + 各访客 IP 浏览次数
function DemoViewStats() {
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get('/ts/demo-share/stats')
      .then((r) => { if (alive && r.data?.success) setStats(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div style={{ marginTop: 14, background: '#f7f8fa', border: '1px solid #eef0f5', borderRadius: 14, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Text strong style={{ fontSize: 14 }}><EyeOutlined /> 浏览量 </Text>
          <Text strong style={{ fontSize: 20, color: '#6366F1' }}>{stats ? stats.totalViews : '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 10 }}>独立访客 IP {stats ? stats.uniqueIps : '—'}</Text>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>访客在演示页输入正确密码成功浏览时计一次（仅管理员可见）</div>
        </div>
        <Button size="small" icon={<EyeOutlined />} onClick={() => setOpen(true)} disabled={!stats || !stats.ips?.length}>
          IP 记录{stats?.ips?.length ? `（${stats.ips.length}）` : ''}
        </Button>
      </div>

      <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={520} title="浏览 IP 记录（仅管理员可见）">
        <Table
          size="small"
          rowKey={(r) => r.ip || Math.random()}
          dataSource={stats?.ips || []}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          columns={[
            { title: 'IP 地址', dataIndex: 'ip', render: (v) => v || '未知' },
            { title: '浏览次数', dataIndex: 'count', width: 90, align: 'center', render: (v) => <Text strong style={{ color: '#6366F1' }}>{v}</Text> },
            { title: '最近浏览', dataIndex: 'last_at', width: 140, render: (v) => (v ? dayjs(v).format('MM-DD HH:mm') : '-') },
          ]}
        />
      </Modal>
    </div>
  );
}

// 演示正文（横幅 + 等级卡 + 收益测算器）——弹窗与公开页共用
export function AgentTierDemoContent({ canShare = false, admin = false }) {
  const [myLevel, setMyLevel] = useState('钻石代理');
  const [directAmount, setDirectAmount] = useState(100000);
  const [recruited, setRecruited] = useState({ 普通代理: 2, 银牌代理: 1, 金牌代理: 0, 钻石代理: 0, 铂金代理: 0 });
  const [downlineAmount, setDownlineAmount] = useState(200000);

  const ownRate = TIER_MAP[myLevel]?.rate || 0.02;
  // 下级TS分成费率随级别：钻石 1%、铂金 2%，其余 0（即不可发展代理）
  const downRate = TIER_MAP[myLevel]?.downRate || 0;
  const canRecruit = downRate > 0;

  const { c1, c2, c3, total } = useMemo(() => {
    const a = (Number(directAmount) || 0) * ownRate; // 直接推荐客户的推荐费
    // 发展代理相关收益仅钻石及以上可得
    const b = canRecruit ? TIERS.reduce((s, t) => s + (Number(recruited[t.key]) || 0) * t.fee * 0.5, 0) : 0; // 推荐代理费用的一半
    const c = canRecruit ? (Number(downlineAmount) || 0) * downRate : 0; // 下级代理总TS金额 × 级别下级费率
    return { c1: a, c2: b, c3: c, total: a + b + c };
  }, [directAmount, ownRate, recruited, downlineAmount, canRecruit, downRate]);

  const animatedTotal = useCountUp(total);
  const downPct = `${(downRate * 100).toFixed(0)}%`;

  const pieData = [
    { name: '直接客户推荐费', value: c1, color: '#6366F1' },
    { name: '推荐代理费用分成', value: c2, color: '#13c2c2' },
    { name: `下级业绩 ${downPct}`, value: c3, color: '#faad14' },
  ].filter((d) => d.value > 0);

  const rows = [
    { label: '直接客户推荐费', desc: `直接推荐客户总额 × ${(ownRate * 100).toFixed(0)}%`, value: c1, color: '#6366F1' },
    { label: '推荐代理费用分成', desc: '所推荐代理升级费用的 50%', value: c2, color: '#13c2c2' },
    { label: '下级业绩分成', desc: canRecruit ? `下级代理总TS金额 × ${downPct}` : '钻石及以上解锁', value: c3, color: '#faad14' },
  ];
  const maxRow = Math.max(1, ...rows.map((r) => r.value));

  return (
    <>
      {/* 顶部横幅 */}
      <div style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)', color: '#fff', padding: '26px 28px', borderRadius: '8px 8px 0 0' }}>
        <div style={{ fontSize: 13, opacity: 0.9, letterSpacing: 2 }}>二次TS · 诚征代理</div>
        <div style={{ fontSize: 26, fontWeight: 800, marginTop: 6 }}>最高可拿 <span style={{ color: '#ffe58f' }}>10%</span> 手续费</div>
        <div style={{ fontSize: 13, opacity: 0.92, marginTop: 6 }}>加入代理体系，客户推荐费 + 团队分成，收益叠加</div>
      </div>

      <div style={{ padding: 24 }}>
        {/* 等级卡（5 个紧凑排一行，minWidth:0 让其自适应收缩、不换行）*/}
        <div style={{ display: 'flex', gap: 6 }}>
          {TIERS.map((t) => {
            const active = t.key === myLevel;
            return (
              <div
                key={t.key}
                onClick={() => setMyLevel(t.key)}
                style={{
                  flex: '1 1 0', minWidth: 0, textAlign: 'center',
                  cursor: 'pointer', borderRadius: 12, padding: '10px 3px', color: '#fff', background: t.grad,
                  boxShadow: active ? `0 6px 14px ${t.color}66` : '0 3px 8px rgba(0,0,0,0.10)',
                  transform: active ? 'translateY(-3px)' : 'none', transition: 'all 0.2s ease',
                  outline: active ? '2px solid #fff' : 'none', outlineOffset: -2,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{t.label}</div>
                <div style={{ fontSize: 19, fontWeight: 800, marginTop: 2, lineHeight: 1 }}>{(t.rate * 100).toFixed(0)}%</div>
                <div style={{ fontSize: 10, opacity: 0.92, marginTop: 3, whiteSpace: 'nowrap' }}>¥{t.fee}</div>
                {t.downRate > 0
                  ? <div style={{ fontSize: 9, marginTop: 4, background: 'rgba(255,255,255,0.28)', borderRadius: 6, padding: '1px 0', whiteSpace: 'nowrap' }}>下级{(t.downRate * 100).toFixed(0)}%</div>
                  : <div style={{ fontSize: 9, marginTop: 4, opacity: 0 }}>·</div>}
              </div>
            );
          })}
        </div>

        {/* 逐级升级补差价说明 */}
        <div style={{ marginTop: 12, fontSize: 12, color: '#597ef7', background: '#f6f9ff', border: '1px solid #e2ecff', borderRadius: 10, padding: '8px 12px' }}>
          💡 支持<b>逐级升级</b>，只需补齐级别差价：{TIERS.slice(1).map((t, i) => `${TIERS[i].label}→${t.label} 补¥${t.fee - TIERS[i].fee}`).join('、')}。
        </div>

        <Divider style={{ margin: '24px 0 18px' }}><Text strong style={{ fontSize: 15 }}>💰 收益测算器</Text></Divider>

        <Row gutter={[24, 20]}>
          {/* 左：输入 */}
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 6 }}>我的代理级别</div>
              <Select value={myLevel} onChange={setMyLevel} style={{ width: '100%' }}>
                {TIERS.map((t) => <Option key={t.key} value={t.key}>{t.label}代理（¥{t.fee} · {(t.rate * 100).toFixed(0)}%）</Option>)}
              </Select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 6 }}>① 我直接推荐客户的总额（元）</div>
              <InputNumber value={directAmount} onChange={setDirectAmount} min={0} step={10000} style={{ width: '100%' }}
                formatter={(v) => `¥ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(v) => v.replace(/[¥,\s]/g, '')} />
            </div>

            {!canRecruit && (
              <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#ad6800' }}>
                🔒 仅 <b>钻石代理（¥1998）及以上</b> 可发展自己的代理人，并享下级TS总额 1% 分成。当前级别只计直接客户推荐费。
              </div>
            )}

            <div style={{ opacity: canRecruit ? 1 : 0.45, pointerEvents: canRecruit ? 'auto' : 'none' }}>
              <div style={{ marginBottom: 6, fontSize: 13, color: '#8c8c8c' }}>② 我推荐了多少下级代理（拿其升级费用的一半）{!canRecruit && <span style={{ color: '#bfbfbf' }}> · 钻石解锁</span>}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                {TIERS.map((t) => (
                  <div key={t.key} style={{ flex: '1 1 70px', minWidth: 64 }}>
                    <div style={{ fontSize: 12, textAlign: 'center', color: t.color, fontWeight: 600 }}>{t.label}</div>
                    <InputNumber disabled={!canRecruit} value={recruited[t.key]} onChange={(v) => setRecruited((s) => ({ ...s, [t.key]: v || 0 }))} min={0} style={{ width: '100%' }} />
                  </div>
                ))}
              </div>

              <div>
                <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 6 }}>③ 下级代理的总TS金额（元 · 拿 {canRecruit ? downPct : '1%~2%'}）{!canRecruit && <span style={{ color: '#bfbfbf' }}> · 钻石解锁</span>}</div>
                <InputNumber disabled={!canRecruit} value={downlineAmount} onChange={setDownlineAmount} min={0} step={10000} style={{ width: '100%' }}
                  formatter={(v) => `¥ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(v) => v.replace(/[¥,\s]/g, '')} />
              </div>
            </div>
          </Col>

          {/* 右：结果 */}
          <Col xs={24} md={12}>
            <div style={{ background: '#faf9ff', border: '1px solid #efeafd', borderRadius: 16, padding: 18, textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: '#8c8c8c' }}>预计可拿推荐费</div>
              <div style={{ fontSize: 40, fontWeight: 800, color: '#722ed1', lineHeight: 1.2 }}>¥{fmt(animatedTotal)}</div>
              <div style={{ height: 150 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData.length ? pieData : [{ name: '暂无', value: 1, color: '#eee' }]} dataKey="value" innerRadius={40} outerRadius={62} paddingAngle={2} stroke="none">
                      {(pieData.length ? pieData : [{ color: '#eee' }]).map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <ReTooltip formatter={(v) => `¥${fmt(v)}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              {rows.map((r) => (
                <div key={r.label} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: r.color, marginRight: 6 }} />{r.label}</span>
                    <Text strong style={{ color: r.color }}>¥{fmt(r.value)}</Text>
                  </div>
                  <div style={{ height: 8, background: '#f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(r.value / maxRow) * 100}%`, background: r.color, borderRadius: 6, transition: 'width 0.5s ease' }} />
                  </div>
                  <div style={{ fontSize: 11, color: '#bbb', marginTop: 2 }}>{r.desc}</div>
                </div>
              ))}
            </div>
          </Col>
        </Row>

        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <Tag color="purple" style={{ fontSize: 12, padding: '2px 10px' }}>
            {canRecruit
              ? `收益 = 直接客户推荐费 + 推荐代理费用的一半 + 下级代理总TS金额的 ${downPct}`
              : '收益 = 直接客户推荐费（钻石拿下级 1% / 铂金拿下级 2%）'}
          </Tag>
        </div>

        {canShare && <DemoSharePanel />}
        {admin && <DemoViewStats />}
      </div>
    </>
  );
}

const AgentTierDemo = ({ open, onClose, admin = false }) => (
  <Modal open={open} onCancel={onClose} footer={null} width={920} title={null} styles={{ body: { padding: 0 } }}>
    <AgentTierDemoContent canShare admin={admin} />
  </Modal>
);

export default AgentTierDemo;
